import {
  Component,
  ComponentRef,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';

import { Store } from '@ngrx/store';

import { SearchFieldDirective } from '@shared/components/search-layout/search-navbar/search-field.directive';
import { paginatorConfiguration } from '@shared/constants/paginator-configuration';
import { StoreFilterDialogComponent } from '@shared/dialogs/store-filter-dialogs/store-filter-dialog/store-filter-dialog.component';
import { SearchFilter } from '@shared/interfaces/search-filter.interface';
import { DialogService } from '@shared/services/dialog.service';

import {
  FilterLocationType,
  FilterStoreActions,
  FilterStoreSelectors,
} from './store/filter-store.slice';

@Component({
  selector: 'app-search-navbar',
  templateUrl: './search-navbar.component.html',
  styleUrls: ['./search-navbar.component.scss'],
})
export class SearchNavbarComponent implements OnInit {
  @Input() searchFieldList: Array<SearchFilter> = [];
  @Input() searchAction!: any;
  @Input() getFilterSelector!: any;
  @Input() storedFilterSelector!: any;
  @Input() updateSearchValueAction!: any;
  @Input() updateStoredFilterAction?: any;
  @Input() pageSize: number = paginatorConfiguration.searchPageSizeOptions[0];
  @Input() canStoreFilter: boolean = false;
  @Input() filterType?: FilterLocationType;
  @ViewChild(SearchFieldDirective, { static: true })
  searchFieldHost!: SearchFieldDirective;
  searchComponentRefs: Array<ComponentRef<any>> = [];

  isFilterStoreDisabled = false;

  filter: any = {
    page: 0,
  };

  selectedFilterId?: string;

  constructor(private store: Store, private dialogService: DialogService) {}

  ngOnInit(): void {
    this.loadComponents();
    if (this.canStoreFilter) {
      this.store.select(this.getFilterSelector).subscribe((filter: any) => {
        this.filter = filter;
        this.checkIfFilterCanStored();
      });
    }

    this.store
      .select(FilterStoreSelectors.selectSelectedStoredFilterId)
      .subscribe((filterId) => {
        this.selectedFilterId = filterId;
        this.checkIfFilterCanStored();
      });
  }

  loadComponents() {
    const viewContainerRef = this.searchFieldHost.viewContainerRef;
    viewContainerRef.clear();
    this.searchComponentRefs = [];
    this.searchFieldList.forEach((component) => {
      const componentRef = viewContainerRef.createComponent(component.name);
      if (component.isGeneric) {
        componentRef.setInput('title', component.title);
        componentRef.setInput('filterUpdater', this.updateSearchValueAction);
        componentRef.setInput(
          'storedFilterSelector',
          this.storedFilterSelector
        );
        componentRef.setInput('property', component.property);
        componentRef.setInput('canStoreFilter', this.canStoreFilter);
        component.data && componentRef.setInput('data', component.data);
      }
      this.searchComponentRefs.push(componentRef);
    });
  }

  openStoreFilterDialog(): void {
    this.dialogService
      .openDialog(StoreFilterDialogComponent, {
        width: '500px',
        maxWidth: '80%',
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          const { page: _, size: __, sort: ___, ...newFilter } = this.filter;
          this.store.dispatch(
            FilterStoreActions.createStoredFilter.trigger({
              request: {
                name: result.filterName,
                filter: newFilter,
                location: this.filterType ?? 'PRICING_FILTER',
              },
            })
          );
        }
      });
  }

  checkIfFilterCanStored(): void {
    const { page: _, size: __, sort: ___, ...newFilter } = this.filter;
    this.isFilterStoreDisabled =
      Object.values(newFilter).some(
        (value) =>
          (!Array.isArray(value) && value != null) ||
          (Array.isArray(value) && value['length'] != 0)
      ) && !this.selectedFilterId;
  }

  search() {
    if (this.selectedFilterId) {
      this.store.dispatch(FilterStoreActions.clearFilters.trigger());
    }

    this.store.dispatch(
      this.searchAction.trigger({
        request: {
          page: 0,
          size: this.pageSize,
        },
      })
    );
  }

  clear() {
    this.searchComponentRefs.forEach((componentRef) => {
      if (componentRef.instance.clear) {
        componentRef.instance.clear();
      }
    });

    this.store.dispatch(
      this.updateSearchValueAction.trigger({
        searchValue: { searchText: '' },
      })
    );

    this.store.dispatch(
      this.updateSearchValueAction.trigger({
        filter: {},
      })
    );

    if (this.updateStoredFilterAction) {
      this.store.dispatch(
        this.updateStoredFilterAction.trigger({ filter: {} })
      );
    }

    this.store.dispatch(FilterStoreActions.clearFilters.trigger());
  }
}
